import { useEffect, useRef, useMemo } from "react";

/**
 * Triggers a refetch callback whenever an SSE event arrives.
 * Connects to the given URL and listens for any of the specified event names.
 *
 * @param debounceMs Optional — when > 0, coalesces bursts of events into a
 * single refetch fired `debounceMs` after the last event (timer resets on
 * each new event). Defaults to 0 (refetch immediately, the original
 * behavior) so existing callers are unaffected.
 */
export function useSSERefetch(
  url: string,
  events: string[],
  refetch: () => void,
  debounceMs = 0
): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stabilize the events array so it doesn't cause reconnections
  const eventsKey = events.join(",");
  const stableEvents = useMemo(() => events, [eventsKey]);

  useEffect(() => {
    if (!url) return;

    const es = new EventSource(url, { withCredentials: true });

    // Listen for ALL events using the generic message handler as a fallback
    // Named event listeners for specific events
    const handler = () => {
      if (debounceMs > 0) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          refetchRef.current();
        }, debounceMs);
      } else {
        refetchRef.current();
      }
    };

    for (const event of stableEvents) {
      es.addEventListener(event, handler);
    }

    // Also listen to the generic "message" event as a catch-all
    // SSE events without a matching addEventListener go to onmessage
    es.onmessage = () => {
      // Don't refetch on generic messages (keepalives etc)
    };

    es.onerror = () => {
      // EventSource auto-reconnects
    };

    return () => {
      for (const event of stableEvents) {
        es.removeEventListener(event, handler);
      }
      es.close();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [url, stableEvents, debounceMs]);
}
